import React from 'react';
import { Card, Col, Row } from 'antd';
import moment from 'moment';

export interface OrderPaymentAddOn {
  key: string;
  label: string;
  selected: boolean;
  quantity: number;
  amount: number;
}

/** Shape of `orderPayment` returned by GET /v1/refund/details/:id. */
export interface OrderPayment {
  orderId: string;
  serviceName: string;
  packageName: string;
  planName: string;
  membersCount: number;
  packageAmount: number;
  addOns: OrderPaymentAddOn[];
  addOnsTotal: number;
  couponCode: string | null;
  couponDiscount: number;
  rewardDiscount: number;
  convenienceFee: number;
  totalDocumitraFee: number;
  amountPaid: number | null;
  currency: string;
  paymentProvider: string | null;
  paymentMethod: string | null;
  transactionId: string | null;
  paymentStatus: string | null;
  paymentDate: string | null;
}

interface RefundOrderPaymentDetailsProps {
  orderPayment: OrderPayment | null;
  titleStyle: React.CSSProperties;
}

const formatMoney = (value: number | null | undefined, currency = 'USD') =>
  `$${Number(value || 0).toFixed(2)} ${currency}`;

const DetailField: React.FC<{ label: string; value: React.ReactNode; className?: string }> = ({
  label,
  value,
  className,
}) => (
  <Col xs={24} sm={12} lg={6}>
    <div className="detail-row">
      <div className="detail-label">{label}</div>
      <div className={`detail-value ${className || ''}`}>{value ?? '--'}</div>
    </div>
  </Col>
);

const RefundOrderPaymentDetails: React.FC<RefundOrderPaymentDetailsProps> = ({
  orderPayment,
  titleStyle,
}) => {
  if (!orderPayment) return null;

  const currency = orderPayment.currency || 'USD';
  const selectedAddOns = (orderPayment.addOns || []).filter(addOn => addOn.selected);
  const couponCode = orderPayment.couponCode?.trim();
  const paymentDate = orderPayment.paymentDate
    ? moment(orderPayment.paymentDate).format('DD/MM/YYYY')
    : '--';

  return (
    <Card
      title={<span style={titleStyle}>Payment Details</span>}
      style={{ marginBottom: 16, borderRadius: 8 }}
      bordered
    >
      <Row gutter={[48, 24]}>
        <DetailField label="Package" value={orderPayment.packageName || '--'} />
        <DetailField label="Plan" value={orderPayment.planName || '--'} />
        <DetailField label="Members" value={orderPayment.membersCount} />
        {/* <DetailField
          label="Amount Paid"
          value={
            orderPayment.amountPaid != null ? formatMoney(orderPayment.amountPaid, currency) : '--'
          }
          className="amount-approved"
        /> */}
        <DetailField label="Order Payment Date" value={paymentDate} />

        <Col xs={24}>
          <div className="refund-purchase-summary">
            <div className="refund-purchase-summary__title">Purchase Summary</div>
            <div className="refund-purchase-summary__rows">
              <div className="refund-purchase-summary__row">
                <span className="refund-purchase-summary__label">Package Amount</span>
                <span className="refund-purchase-summary__amount">
                  {formatMoney(orderPayment.packageAmount, currency)}
                </span>
              </div>
              <div className="refund-purchase-summary__divider" />
              {selectedAddOns.length ? (
                selectedAddOns.map((addOn, index) => (
                  <React.Fragment key={addOn.key}>
                    {index > 0 && <div className="refund-purchase-summary__divider" />}
                    <div className="refund-purchase-summary__row">
                      <span className="refund-purchase-summary__label">
                        {addOn.label}
                        <span className="refund-purchase-summary__quantity">
                          Qty: {addOn.quantity || '--'}
                        </span>
                      </span>
                      <span className="refund-purchase-summary__amount">
                        + {formatMoney(addOn.amount, currency)}
                      </span>
                    </div>
                  </React.Fragment>
                ))
              ) : (
                <div className="refund-purchase-summary__empty">No add-ons</div>
              )}

              {couponCode && (
                <>
                  <div className="refund-purchase-summary__divider" />
                  <div className="refund-purchase-summary__row">
                    <span className="refund-purchase-summary__label">
                      Coupon Amount <span style={{ color: 'red' }}>( {couponCode} )</span>
                    </span>
                    <span className="refund-purchase-summary__amount">
                      - {formatMoney(orderPayment.couponDiscount, currency)}
                    </span>
                  </div>
                </>
              )}
              {orderPayment.rewardDiscount > 0 && (
                <>
                  <div className="refund-purchase-summary__divider" />
                  <div className="refund-purchase-summary__row">
                    <span className="refund-purchase-summary__label">Reward Points Discount</span>
                    <span className="refund-purchase-summary__amount">
                      - {formatMoney(orderPayment.rewardDiscount, currency)}
                    </span>
                  </div>
                </>
              )}
              <div className="refund-purchase-summary__divider" />
              <div className="refund-purchase-summary__row">
                <span className="refund-purchase-summary__label">Convenience Fee</span>
                <span className="refund-purchase-summary__amount">
                  + {formatMoney(orderPayment.convenienceFee, currency)}
                </span>
              </div>
              {/*
              <div className="refund-purchase-summary__total">
                <span>Total Add-ons</span>
                <span>{formatMoney(orderPayment.addOnsTotal, currency)}</span>
              </div>
              */}
              <div className="refund-purchase-summary__total">
                <span>Total Documitra Fee</span>
                <span>{formatMoney(orderPayment.totalDocumitraFee, currency)}</span>
              </div>
            </div>
          </div>
        </Col>

        {/* <DetailField label="Payment Gateway" value={orderPayment.paymentProvider || '--'} /> */}
        {/* <DetailField label="Transaction ID" value={orderPayment.transactionId || '--'} /> */}
        {/* <DetailField label="Payment Status" value={orderPayment.paymentStatus || '--'} /> */}
      </Row>
    </Card>
  );
};

export default RefundOrderPaymentDetails;
